import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiArrowUpRight } from "react-icons/hi2";

import fordestLogo from "./images/fordest.png";
import delveLogo from "./images/delve.png";
import smarktLogo from "./images/smarkt-icon.svg";
import venngageLogo from "./images/venngage-icon.svg";

const experiences = [
  {
    role: "Frontend Developer",
    company: "Smarkt",
    location: "Remote",
    flag: "ma",
    logo: smarktLogo,
    logoBg: "bg-black",
    logoContain: true,
    period: "2025 — May 2026",
    summary:
      "Collaborating with the in‑house designer on the Smarkt rebrand and helping ship internal projects from design handoff to production — turning Figma into polished, performant, quality code.",
    stack: ["Next.js", "TypeScript", "Tailwind"],
    link: "https://smarkt.tech/",
  },
  {
    role: "Lead Frontend Engineer",
    company: "Tessa",
    location: "Remote",
    flag: "ng",
    logo: "/images/Tessa logo - 320.png",
    logoContain: true,
    period: "2025 — Present",
    summary:
      "Leading frontend for an AI support platform that plugs into existing helpdesks — owning architecture, launch surfaces and polish.",
    stack: ["Next.js", "Tailwind", "Framer Motion"],
    link: "https://tessahq.com/",
  },
  {
    role: "Frontend Software Developer",
    company: "Venngage",
    location: "Canada (Remote)",
    flag: "ca",
    logo: venngageLogo,
    logoContain: true,
    period: "Aug — Dec 2024",
    summary:
      "Contributed to frontend feature work on a leading Canadian information design platform, focused on accessibility and core editor improvements.",
    stack: ["React", "TypeScript", "Redux"],
    link: "https://venngage.com/",
  },
  {
    role: "CTO & Lead Engineer",
    company: "Fordest Technologies",
    location: "Lagos, Nigeria",
    flag: "ng",
    logo: fordestLogo,
    period: "2023 — Present",
    summary:
      "Running a product studio shipping scalable web and mobile apps — from UX to deployment — for startups and established teams.",
    stack: ["React", "Next.js", "React Native", "Node.js"],
    link: "http://fordestech.com/",
  },
  {
    role: "Frontend Web & Mobile Developer",
    company: "Orlo Ltd",
    location: "Lagos, Nigeria",
    flag: "ng",
    logo: "/images/170772469.png",
    logoContain: true,
    period: "2024",
    summary:
      "Marketplace connecting drycleaners with clients — web and mobile interfaces for pickup/delivery logistics and in‑app payments.",
    stack: ["React Native", "Next.js", "Payments"],
    link: "https://github.com/Orlomgmt",
  },
  {
    role: "Fullstack Intern",
    company: "HNG — Delve",
    location: "Distributed team",
    flag: "globe",
    logo: delveLogo,
    period: "3 months · 2024",
    summary:
      "Shipped features on an interactive language‑learning product with a fully remote team.",
    stack: ["React", "TypeScript", "Node.js"],
    link: "https://delve.fun/",
  },
];

const Row = ({ exp, index, isOpen, onToggle }) => {
  return (
    <motion.li
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: index * 0.04, ease: [0.2, 0.8, 0.2, 1] }}
      onMouseEnter={() => {
        if (window.innerWidth >= 1024) onToggle(index);
      }}
      onMouseLeave={() => {
        if (window.innerWidth >= 1024) onToggle(null);
      }}
      className="relative border-t border-black/10 dark:border-white/10 last:border-b"
    >
      <button
        type="button"
        onClick={() => onToggle(isOpen ? null : index)}
        aria-expanded={isOpen}
        className="group block w-full text-left relative px-2 py-5 lg:py-7"
      >
        <div className="grid grid-cols-[70px,1fr,auto] lg:grid-cols-[160px,1fr,1fr,auto] items-center gap-3 lg:gap-8">
          <div className="text-[11px] lg:text-xs tracking-wider opacity-50 font-mono">
            {exp.period}
          </div>

          <div className="flex items-center gap-3 lg:gap-4 min-w-0">
            <div className={`hidden lg:flex flex-none w-9 h-9 rounded-md items-center justify-center overflow-hidden border border-black/10 dark:border-white/10 ${exp.logoBg || "bg-white dark:bg-black/40"}`}>
              {exp.logo ? (
                <img
                  src={exp.logo}
                  alt=""
                  aria-hidden
                  className={`w-full h-full ${exp.logoContain ? "object-contain p-1.5" : "object-cover"}`}
                />
              ) : (
                <span className="text-[13px] font-semibold opacity-80">
                  {exp.monogram || exp.company.charAt(0)}
                </span>
              )}
            </div>
            <h3 className="text-lg lg:text-2xl font-medium tracking-tight leading-tight truncate">
              {exp.role}
            </h3>
          </div>

          <div className="hidden lg:flex items-baseline gap-2 min-w-0">
            <span className="text-lg lg:text-2xl font-medium tracking-tight truncate">
              {exp.company}
            </span>
            {exp.flag && exp.flag !== "globe" && (
              <img
                src={`https://flagcdn.com/w40/${exp.flag}.png`}
                srcSet={`https://flagcdn.com/w80/${exp.flag}.png 2x`}
                alt={exp.flag}
                className="inline-block w-5 h-auto rounded-sm border border-black/10 dark:border-white/10"
              />
            )}
            <span className="text-xs opacity-40 truncate">· {exp.location}</span>
          </div>

          <motion.div
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="flex-none opacity-40 group-hover:opacity-100 group-hover:text-accentClr dark:group-hover:text-accentClrDark transition-colors"
          >
            <HiArrowUpRight size={20} />
          </motion.div>
        </div>

        <div className="lg:hidden mt-2 text-xs opacity-60 flex items-center gap-1.5">
          <span>{exp.company}</span>
          {exp.flag && exp.flag !== "globe" && (
            <img
              src={`https://flagcdn.com/w40/${exp.flag}.png`}
              alt={exp.flag}
              className="inline-block w-4 h-auto rounded-[2px]"
            />
          )}
          <span>· {exp.location}</span>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden px-2"
          >
            <div className="grid grid-cols-1 lg:grid-cols-[160px,1fr] gap-4 lg:gap-8 pb-5 lg:pb-6">
              <div />
              <div className="max-w-2xl">
                <p className="text-[14px] lg:text-[15px] opacity-70 leading-relaxed">
                  {exp.summary}
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {exp.stack.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] font-mono tracking-wide uppercase px-2 py-0.5 border border-black/10 dark:border-white/10 opacity-70"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                {exp.link && (
                  <a
                    href={exp.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-4 text-xs font-mono tracking-wider uppercase text-accentClr dark:text-accentClrDark hover:opacity-80 transition-opacity"
                  >
                    Visit site <HiArrowUpRight size={14} />
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.span
        initial={false}
        animate={{ scaleX: isOpen ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
        style={{ transformOrigin: "left" }}
        className="absolute left-0 right-0 top-0 h-px bg-accentClr dark:bg-accentClrDark pointer-events-none"
      />
    </motion.li>
  );
};

const Experience = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const handleToggle = (index) => {
    setActiveIndex(index);
  };

  return (
    <section id="experience" className="text-textClr dark:text-textClrDark w-full mt-16 pb-24">
      <div className="lg:w-[82%] w-[90%] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase opacity-50 font-mono">
              Experience
            </p>
            <h2 className="text-4xl lg:text-6xl font-semibold leading-[1.05] tracking-tight mt-4">
              Teams &amp; products I've
              <br />
              <span className="italic font-serif text-accentClr dark:text-accentClrDark">
                shipped
              </span>{" "}
              with.
            </h2>
          </div>
          <p className="text-sm opacity-60 lg:max-w-xs lg:text-right">
            Years of building high-performance interfaces — from industrial-scale
            educational platforms to winning blockchain and AI solutions.
          </p>
        </div>

        <ul className="relative">
          {experiences.map((exp, i) => (
            <Row
              key={`${exp.company}-${i}`}
              exp={exp}
              index={i}
              isOpen={activeIndex === i}
              onToggle={handleToggle}
            />
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Experience;
